import { faUpload, faDownload, faCircleXmark, faSpinner } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useTheme } from '../../../../context/ThemeProvider'
import { ImageConfig } from './ImageConfig'
import { useState, useRef } from 'react'

import Modal from '../../../../components/Modal'
import useAxiosPrivateKIS from '../../../../hooks/useAxiosPrivateKIS'
import useAuth from '../../../../hooks/useAuth'

const UploadStaff = ({ setOpenCsv, policyData, onUploaded, setUploadMessage, setUploadWarnings, setShowModal, setRows }) => {
  const theme = useTheme()
  const axios = useAxiosPrivateKIS()
  const id = policyData?.policy_id

  const { createInfo } = useAuth()

  const style = {
    backgroundColor: theme.backgroundColor,
    color: theme.textColor,
  }

  const wrapperRef = useRef(null)

  const [errorMsg, setErrorMsg] = useState('')
  const [isSuccess, setIsSuccess] = useState(false)
  const [fileList, setFileList] = useState([])
  const [showFiles, setShowFiles] = useState([])
  const [commAttachments, setCommAttachments] = useState([])
  const [selectedFile, setSelectedFile] = useState()
  const [isUploadingCsv, setIsUploadingCsv] = useState(false)
  const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false)

  const onDragEnter = () => wrapperRef.current.classList.add('dragover')
  const onDragLeave = () => wrapperRef.current.classList.remove('dragover')
  const onDragOver = (e) => {
    e.stopPropagation()
    e.preventDefault()
  }

  const onDrop = (e) => {
    e.preventDefault()
    wrapperRef.current.classList.remove('dragover')
    onFileDrop(e.nativeEvent.dataTransfer.files)
  }

  const allowedFileTypes = [
    '.doc',
    '.docx',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    '.xls',
    '.xlsx',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'text/csv',
    '.csv',
  ]

  const isValidFileType = (file) => {
    const extension = `.${file.name.split('.').pop()}`
    const mimeType = file.type
    return (
      allowedFileTypes.includes(extension) ||
      allowedFileTypes.includes(mimeType)
    )
  }

  const onFileUpload = async (e) => {
    e.preventDefault()
    const newFile = e.target.files[0]

    if (!newFile.name.toLowerCase().includes('staff_import_template')) {
      fileRemove(newFile)
      return createInfo('error', 'Please download the xlsx template and use this to upload your staff.')
    }

    if (newFile && isValidFileType(newFile)) {
      const updatedList = [...fileList, newFile]
      setFileList(updatedList)
    } else {
      createInfo('error', 'Invalid file type. Please select a valid file.')
    }
  }

  const onFileDrop = async (files) => {
    const newFile = files[0]

    if (!newFile.name.toLowerCase().includes('staff_import_template')) {
      fileRemove(newFile)
      return createInfo('error', 'Please download the xlsx template and use this to upload your staff.')
    }

    if (newFile && isValidFileType(newFile)) {
      const updatedList = [...fileList, newFile]
      setFileList(updatedList)
    } else {
      createInfo('error', 'Invalid file type')
      return
    }
  }

  const downloadTemplate = async () => {
    setIsDownloadingTemplate(true)
    try {
      const response = await axios.get(`/api/v1/staff-hub/staffs/import-template/${policyData?.policy_id}?api_token=${process.env.REACT_APP_KIS_TOKEN}`, {
        responseType: 'blob',
      })

      const contentDisposition = response?.headers?.['content-disposition'] || ''
      const filenameMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i)
      const downloadName = decodeURIComponent(filenameMatch?.[1] || filenameMatch?.[2] || 'staff_import_template.csv')
      const blob = response.data instanceof Blob
        ? response.data
        : new Blob([response.data], { type: response?.headers?.['content-type'] || 'text/csv' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', downloadName)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Error downloading template:', error)
    } finally {
      setIsDownloadingTemplate(false)
    }
  }

  const uploadDocuments = async () => {
    setIsUploadingCsv(true)

    try {
      const file = fileList[0]
      const policyData = JSON.parse(localStorage.getItem('policyData') || '{}')
      const orgType = JSON.parse(localStorage.getItem('orgType') || '{}')

      const policyId = policyData?.policy_id
      const orgId = policyData?.policyable_id

      if (!policyId || !orgId) {
        createInfo('error', 'Missing policy or organisation context')
        return
      }

      const csrf =
        document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
        || window._csrf_token
        || '';

      const fd = new FormData()
      fd.append('file', file, file.name)
      fd.append('policyId', policyId)
      fd.append('orgid', orgId)
      fd.append('appendReplace', 'append')
      if (csrf) fd.append('_csrf_token', csrf)
      const res = await axios.post(`/api/v1/staff-hub/staffs/csvupload?api_token=${process.env.REACT_APP_KIS_TOKEN}`, fd)
      const warnings = res?.data?.warnings ?? []
      const results = res?.data?.results ?? []
      const rows = res?.data?.results ?? []
      setFileList([])

      let message = '';
      if (warnings?.length > 0) {
        message = `Upload completed. ${res?.data?.skippedRows} / ${res?.data?.skippedRows + (res?.data?.results?.length ?? 0)} rows were skipped.`;
      } else if (warnings?.length === 0) {
        message = 'All rows successfully completed'
      }
      setRows(rows)
      setUploadMessage(message)

      if (res?.data?.warnings && res?.data?.warnings.length > 0) {
          setUploadWarnings(res?.data?.warnings)
          setShowModal(true)
      } else {
          setUploadWarnings(null)
      }

      // warnings.forEach(w => createInfo('warning', w))
      // createInfo('success', `Uploaded ${results.length} record(s)`)
      setFileList([])
      onUploaded()

    } catch (error) {
      const msg = error?.response?.data?.message || 'Failed to upload staff'
      createInfo('error', msg)
      console.error(error)
    } finally {
      setIsUploadingCsv(false)
    }

    setOpenCsv(false)
  }


  const fileRemove = (file) => {
    const updatedList = [...fileList]
    updatedList.splice(fileList.indexOf(file), 1)
    setFileList(updatedList)
  }



  const deleteFile = async (file) => {
    try {
      const response = await axios.get(`/api/clients/delete-document/${file.id}`)

      if (!response.data.error) {
        const updatedList = [...showFiles]
        updatedList.splice(showFiles.indexOf(file), 1)
        setShowFiles(updatedList)

        createInfo('success', 'Document removed')
      } else {
        createInfo('error', 'Could not remove document')
      }
    } catch (error) {
      createInfo('error', 'Could not remove document')
    }
  }

  const deleteAttachment = async (file) => {
    try {
      const response = await axios.get(
        `/api/clients/${id}/delete-attachment/${file.id}`
      )

      if (!response.data.error) {
        const updatedList = [...commAttachments]
        updatedList.splice(commAttachments.indexOf(file), 1)
        setCommAttachments(updatedList)

        createInfo('success', 'Document removed')
      } else {
        createInfo('error', 'Could not remove document')
      }
    } catch (error) {
      createInfo('error', 'Could not remove document')
    }
  }

  return (
    <div className="flex flex-col">
      <div className='px-6 py-2 text-sas-dark'>
        <div>Please follow the instructions below to correctly import your staff:</div>
        <ol className='list-decimal pl-10 text-sm'>
          <li>Download the template from the "Download CSV Template" button.</li>
          <li>Do not amend or change the column names.</li>
          <li>You can add new staff members by adding a new row to the bottom of the table.</li>
          <li>Please only upload one csv file at a time.</li>
        </ol>
        <p className='text-sm italic mt-5 '>Notes:</p>
        <ol className='text-sm italic list-disc pl-10'>
            <li>Only records with a Forename, Surname, Date of Birth, FTE, and Staff Type  will be uploaded to the system.</li>
            <li>Include the Staff Type and FTE to automatically add them to the correct staff groups.
            The staff type can be either its full name (check the spelling) or its corresponding number.
            Once you are happy with the data provided select 'Upload Staff' below.</li>
            <li>Dates must be in the 'DD/MM/YYYY' or 'YYYY-MM-DD' format.</li>
        </ol>

      </div>

      <div className="col-span-full pb-20">
        <label htmlFor="file-upload"
          ref={wrapperRef}
          onDragEnter={onDragEnter}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
          onDragOver={onDragOver}
          className="text-center p-3 cursor-pointer"
        >
          <div className="border border-dashed border-gray-300 rounded-md mx-6">
            <img src="/images/laptop.png" alt="Upload" className="mx-auto h-44 mb-3" />
            <p className="text-sas font-medium">Drag and Drop or click to upload a file</p>
            <p className="text-sm text-gray-500 pb-6">CSV or XLS</p>
          </div>
          <input
            type="file"
            id="file-upload"
            name="file-upload"
            accept=".csv,text/csv,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            onChange={onFileUpload}
            disabled={isUploadingCsv || isDownloadingTemplate}
            className="hidden"
          />
        </label>

        <div className="grid grid-cols-3 gap-3">
          {fileList.map((item, index) => (
            <div
              key={index}
              className="drop-file-preview__item flex items-center justify-between "
            >
              <div className="flex items-center">
                <img
                  src={
                    ImageConfig[item.type.split('/')[1]] ||
                    ImageConfig['default']
                  }
                  alt=""
                  className="w-12"
                />

                <div className="drop-file-preview__item__info">
                  <p className="break-words">{item.name}</p>
                  <p className='text-sm'>{item.size}</p>
                </div>
              </div>

              <span
                className=" hover:text-red-700 drop-file-preview__item__del"
                onClick={() => fileRemove(item)}
              >
                <FontAwesomeIcon
                  icon={faCircleXmark}
                  className=" text-gray-700 hover:text-red-700 w-6 h-6"
                />
              </span>
            </div>
          ))}
        </div>
        
        <div className="px-6 pb-4 flex items-center justify-between absolute bottom-2 w-full">
          <button onClick={uploadDocuments} disabled={isUploadingCsv || isDownloadingTemplate || fileList.length === 0} className={`inline-flex items-center border border-sas bg-white text-sas rounded-md overflow-hidden text-sm font-medium ${(isUploadingCsv || isDownloadingTemplate || fileList.length === 0) ? 'opacity-50 cursor-not-allowed' : ''}`}>
            <span className="px-4 py-2 inline-flex items-center gap-2">
              {isUploadingCsv ? (
                <>
                  Loading...
                  <FontAwesomeIcon icon={faSpinner} spin />
                </>
              ) : (
                'Upload CSV'
              )}
            </span>
            <span className="bg-sas text-white px-3 py-2">
              <FontAwesomeIcon icon={faUpload} />
            </span>
          </button>

          <button type="button" onClick={downloadTemplate} disabled={isUploadingCsv || isDownloadingTemplate} className={`inline-flex items-center border border-sas text-sas bg-white rounded-md overflow-hidden text-sm font-medium ${(isUploadingCsv || isDownloadingTemplate) ? 'opacity-50 cursor-not-allowed' : ''}`}>
            <span className="px-4 py-2 inline-flex items-center gap-2">
              {isDownloadingTemplate ? (
                <>
                  Loading...
                  <FontAwesomeIcon icon={faSpinner} spin />
                </>
              ) : (
                'Download CSV Template'
              )}
            </span>
            <span className="bg-sas text-white px-3 py-2">
              <FontAwesomeIcon icon={faDownload} />
            </span>
          </button>
        </div>
      </div>
    </div>
  )
}

export default UploadStaff
